import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  TimeslotStatusEnum,
  TimeslotWorkflowActionEnum,
} from '@sport-activities/sdk';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { AlertModalComponent } from '~/component/modal/alert/alert.component';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerTimeslotEditionSharedSelectTimeslotsComponent } from '~/organizer/timeslot-edition/components/shared/select-timeslots/select-timeslots.component';
import { TimeslotUpdateService } from '~/organizer/timeslot-edition/services/timeslot-update.service';

@Component({
  selector: 'app-organizer-timeslot-edition-delete-selected-timeslots',
  templateUrl: './delete-selected-timeslots.component.html',
})
export class OrganizerTimeslotEditionDeleteSelectedTimeslotsComponent
  implements OnInit, OnDestroy
{
  @ViewChild(OrganizerTimeslotEditionSharedSelectTimeslotsComponent)
  public child: OrganizerTimeslotEditionSharedSelectTimeslotsComponent;
  @ViewChild('deleteTimeslotWarning')
  public deleteTimeslotWarningModal: AlertModalComponent;

  private readonly subscriptions = new Subscription();

  public activity: Activity;
  public selectedTimeslots = new FormControl();
  public timeslots: Timeslot[] = [];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly timeslotUpdateService: TimeslotUpdateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    const { upcomingTimeslotsData } = this.activatedRoute.snapshot.data;

    this.timeslots = upcomingTimeslotsData.upcomingTimeslots;
    this.activity = upcomingTimeslotsData.activity;

    this.selectedTimeslots.setValue({
      'select-all': false,
      timeslots: { [this.timeslots?.[0]?.identifier]: true },
    });
  }

  public get previousUrl(): string {
    return this.child?.overlayStatus === SuccessStatusEnum.SUCCESS
      ? `/organizer.activity.manage/${this.activity?.sku?.toString()}`
      : `/organizer.timeslot.manage/${this.timeslots?.[0]?.identifier}`;
  }

  private timeslotsToUpdate(): Timeslot[] {
    return this.timeslotUpdateService.getTimeslotsToUpdate(
      this.selectedTimeslots,
      this.timeslots
    );
  }

  public onSubmit(): void {
    if (
      this.timeslotsToUpdate().some(
        (timeslot) =>
          timeslot.maximumAttendeeCapacity !==
          timeslot.remainingAttendeeCapacity
      )
    ) {
      this.deleteTimeslotWarningModal.show();
    } else {
      this.updateTimeslots();
    }
  }

  public updateTimeslots(): void {
    this.child.isOverlayDisplayed = true;
    this.deleteTimeslotWarningModal.hide();

    const timeslots = this.timeslotsToUpdate().map(({ identifier }) => ({
      identifier,
      action: 'cancel' as TimeslotWorkflowActionEnum.CANCEL,
    }));

    this.subscriptions.add(
      this.activityService
        .updateTimeslotWorkflowBulk(this.activity.sku, timeslots)
        .pipe(
          mergeMap(() => {
            return this.activityService
              .getTimeslot(timeslots[0]?.identifier)
              .pipe(
                repeatUntil(
                  (cancelledTimeslot) =>
                    cancelledTimeslot.status ===
                    ('CANCELED' as TimeslotStatusEnum)
                )
              );
          })
        )
        .subscribe(() => {
          this.child.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }
}
